import { useState } from "react";
import {
  Mail,
  UserPlus,
  Loader2,
  X,
  Shield,
  UserCheck,
} from "lucide-react";
import { supabase, requireOrganizationId, type OrgRole } from "../lib/supabase";
import { useAuth } from "../hooks/useAuth";
import { useSubscription } from "../hooks/useSubscription";
import { useAuditLog } from "../hooks/useAuditLog";
import { useToast } from "./ui/ToastModal";
import { logError } from "../lib/logError";

interface InviteMemberDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInvited: () => void;
}

const ROLE_OPTIONS: { value: OrgRole; label: string; desc: string; icon: typeof Shield }[] = [
  {
    value: "admin",
    label: "Admin",
    desc: "Can manage projects, settings, and view financials",
    icon: Shield,
  },
  {
    value: "member",
    label: "Member",
    desc: "Can create and edit projects, submit claims",
    icon: UserCheck,
  },
  {
    value: "viewer",
    label: "Viewer",
    desc: "Read-only access to projects and reports",
    icon: UserCheck,
  },
];

export function InviteMemberDialog({
  isOpen,
  onClose,
  onInvited,
}: InviteMemberDialogProps) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<OrgRole>("member");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { canAddMember } = useSubscription();
  const { log: auditLog } = useAuditLog();

  if (!isOpen) return null;

  const orgId = requireOrganizationId();

  const handleInvite = async () => {
    setError(null);

    // Validate
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      setError("Please enter an email address.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    setSending(true);
    try {
      // Tier enforcement: check member limit
      const { count: memberCount } = await supabase
        .from("organization_members")
        .select("id", { count: "exact", head: true })
        .eq("organization_id", orgId);

      if (!canAddMember(memberCount ?? 0)) {
        setError("Member limit reached for your current plan. Please upgrade to invite more team members.");
        setSending(false);
        return;
      }

      // Check for existing member: first look up profile by email
      // then verify org membership (handled below via existingProfile)

      // Check if already invited (pending)
      const { data: existingInvite } = await supabase
        .from("organization_invitations")
        .select("id, accepted_at")
        .eq("organization_id", orgId)
        .eq("email", trimmedEmail)
        .is("accepted_at", null)
        .maybeSingle();

      if (existingInvite) {
        setError("This email already has a pending invitation.");
        setSending(false);
        return;
      }

      // Check if user already exists in profiles
      const { data: existingProfile } = await supabase
        .from("profiles")
        .select("id")
        .eq("email", trimmedEmail)
        .maybeSingle();

      if (existingProfile) {
        // Check if already a member by user_id
        const { data: memberCheck } = await supabase
          .from("organization_members")
          .select("id")
          .eq("organization_id", orgId)
          .eq("user_id", existingProfile.id)
          .maybeSingle();

        if (memberCheck) {
          setError("This user is already a member of this organization.");
          setSending(false);
          return;
        }
      }

      // Generate token
      const token = crypto.randomUUID();

      // Set expiry to 7 days from now
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      // Create invitation
      const { error: insertError } = await supabase
        .from("organization_invitations")
        .insert({
          organization_id: orgId,
          email: trimmedEmail,
          role,
          token,
          invited_by: user!.id,
          expires_at: expiresAt.toISOString(),
        });

      if (insertError) throw insertError;

      addToast({
        type: "success",
        title: "Invitation created",
        message: `Invitation for ${trimmedEmail} has been recorded. Share the invite token to complete onboarding.`,
      });

      // Audit log
      auditLog({
        action: "member.invite",
        entity_type: "invitation",
        metadata: { email: trimmedEmail, role },
      });

      // Reset form
      setEmail("");
      setRole("member");
      onInvited();
      onClose();
    } catch (err) {
      logError("[Invite] Error:", err);
      setError("Failed to create invitation. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !sending) {
      handleInvite();
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
      />

      {/* Dialog */}
      <div
        style={{
          position: "relative",
          background: "var(--bg-card)",
          borderRadius: 16,
          boxShadow: "0 25px 50px rgba(0,0,0,0.25)",
          width: "100%",
          maxWidth: 480,
          animation: "modalSlideIn 0.2s ease-out",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid var(--border-color)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-sm)",
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "var(--border-radius-md)",
                background: "rgba(59,130,246,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#3b82f6",
              }}
            >
              <UserPlus size={18} />
            </div>
            <div>
              <h3
                style={{
                  margin: 0,
                  fontSize: "var(--font-size-lg)",
                  fontWeight: 600,
                  color: "var(--text-primary)",
                }}
              >
                Invite Team Member
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: "var(--font-size-xs)",
                  color: "var(--text-muted)",
                }}
              >
                Send an invitation to join this organization
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: "var(--bg-secondary)",
              border: "none",
              borderRadius: 8,
              padding: 8,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--text-muted)",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 24 }}>
          {/* Email Input */}
          <div style={{ marginBottom: "var(--spacing-lg)" }}>
            <label
              style={{
                display: "block",
                fontSize: "var(--font-size-sm)",
                fontWeight: 500,
                color: "var(--text-primary)",
                marginBottom: "var(--spacing-xs)",
              }}
            >
              Email Address
            </label>
            <div style={{ position: "relative" }}>
              <Mail
                size={16}
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--text-muted)",
                }}
              />
              <input
                type="email"
                placeholder="colleague@company.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                onKeyDown={handleKeyDown}
                autoFocus
                style={{
                  width: "100%",
                  paddingLeft: 36,
                  height: 44,
                  border: `1px solid ${error ? "#ef4444" : "var(--border-color)"}`,
                  borderRadius: "var(--border-radius-md)",
                  background: "var(--bg-secondary)",
                  color: "var(--text-primary)",
                  fontSize: "var(--font-size-sm)",
                  transition: "border-color 0.15s ease",
                }}
              />
            </div>
          </div>

          {/* Role Selection */}
          <div style={{ marginBottom: "var(--spacing-lg)" }}>
            <label
              style={{
                display: "block",
                fontSize: "var(--font-size-sm)",
                fontWeight: 500,
                color: "var(--text-primary)",
                marginBottom: "var(--spacing-sm)",
              }}
            >
              Role
            </label>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--spacing-xs)",
              }}
            >
              {ROLE_OPTIONS.map((opt) => {
                const isSelected = role === opt.value;
                const RoleIcon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setRole(opt.value)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--spacing-sm)",
                      padding: "var(--spacing-sm) var(--spacing-md)",
                      border: `1.5px solid ${isSelected ? "var(--color-primary)" : "var(--border-color)"}`,
                      borderRadius: "var(--border-radius-md)",
                      background: isSelected
                        ? "rgba(30,115,190,0.06)"
                        : "transparent",
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        background: isSelected
                          ? "rgba(30,115,190,0.12)"
                          : "var(--bg-secondary)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: isSelected
                          ? "var(--color-primary)"
                          : "var(--text-muted)",
                        flexShrink: 0,
                      }}
                    >
                      <RoleIcon size={14} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: "var(--font-size-sm)",
                          fontWeight: 500,
                          color: "var(--text-primary)",
                        }}
                      >
                        {opt.label}
                      </div>
                      <div
                        style={{
                          fontSize: "var(--font-size-xs)",
                          color: "var(--text-muted)",
                        }}
                      >
                        {opt.desc}
                      </div>
                    </div>
                    <div
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        border: `2px solid ${isSelected ? "var(--color-primary)" : "var(--border-color)"}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {isSelected && (
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: "var(--color-primary)",
                          }}
                        />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div
              style={{
                padding: "var(--spacing-sm) var(--spacing-md)",
                background: "rgba(239,68,68,0.06)",
                border: "1px solid rgba(239,68,68,0.15)",
                borderRadius: "var(--border-radius-md)",
                fontSize: "var(--font-size-xs)",
                color: "#ef4444",
                marginBottom: "var(--spacing-md)",
              }}
            >
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid var(--border-color)",
            display: "flex",
            justifyContent: "flex-end",
            gap: "var(--spacing-sm)",
          }}
        >
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={sending}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleInvite}
            disabled={sending || !email.trim()}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-xs)",
            }}
          >
            {sending ? (
              <>
                <Loader2
                  size={16}
                  style={{ animation: "spin 1s linear infinite" }}
                />
                Sending...
              </>
            ) : (
              <>
                <UserPlus size={16} />
                Send Invitation
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
